import {
  AbortError,
  GUID,
  PriorityTaskQueue,
  TaskManager,
} from "@faro-lotv/foundation";
import {
  ApiResponseError,
  DynamicMapTileRequest,
} from "@faro-lotv/service-wires";
import { ClampToEdgeWrapping, LinearFilter, Texture } from "three";
import { TileKey } from "./tile-math";

/** A tile of a specific scan subset */
export type ScanTile = TileKey & {
  /** Compact scan list the tile is rendered for */
  scans: string;
};

/** A tile a layer wants to have loaded */
export type WantedTile = ScanTile & {
  /** Higher values are loaded first */
  priority: number;
};

export type DynamicMapTileStoreOptions = {
  /** The id of the project */
  projectId: GUID;
  /** The id of the DynamicTiledMap element */
  elementId: GUID;
  /** The (signed) url of the metadata, sent to the backend with each tile request */
  metadataUrl: string;
  /** Fetch the encoded image of a tile */
  fetchTile(request: DynamicMapTileRequest, signal: AbortSignal): Promise<Blob>;
  /** Re-sign the metadata url, used once if the backend fails to render tiles */
  refreshMetadataUrl?(): Promise<string>;
  /** Decode a tile image into a texture @default decodeTileTexture */
  decode?(blob: Blob): Promise<Texture>;
  /** Max bytes of decoded tiles to keep in memory */
  budgetBytes: number;
  /** Decoded size of one tile in bytes */
  tileBytes: number;
};

/** Queue shared by all dynamic maps, to limit the number of parallel tile requests */
const tileQueue = new PriorityTaskQueue<string, Texture>({
  maxParallelTasks: 6,
});

type PendingTile = WantedTile & {
  key: string;
  taskId: string;
  controller: AbortController;
};

const UNAUTHORIZED = 401;
const FORBIDDEN = 403;
const SERVER_ERROR = 500;

/**
 * @returns the cache key of a tile
 * @param tile to compute the key for
 */
export function tileCacheKey({ scans, level, x, y }: ScanTile): string {
  return `${scans}|${level}|${x}|${y}`;
}

/**
 * Loads, deduplicates, prioritizes and caches (LRU) the tiles of one dynamic map.
 * Multiple layers (e.g. main view and minimap) can share one store.
 */
export class DynamicMapTileStore {
  /** Loaded textures, in least recently used order */
  #textures = new Map<string, Texture>();
  /** Requests in progress, by cache key */
  #pending = new Map<string, PendingTile>();
  /** Requests in progress, by task id: a tile can be requested again while its aborted task is ending */
  #tasks = new Map<string, PendingTile>();
  #nextTaskId = 0;
  /** Tiles that failed permanently and should not be requested again */
  #failedTiles = new Set<string>();
  #wanted = new Map<symbol, Set<string>>();
  #manager: TaskManager<string, Texture>;
  #metadataUrl: string;
  #metadataRefreshed = false;
  #stopped = false;

  /** @param options to configure this store */
  constructor(private options: DynamicMapTileStoreOptions) {
    this.#metadataUrl = options.metadataUrl;
    this.#manager = tileQueue.createManager({
      run: (taskId) => this.#load(taskId),
      priority: (taskId) => this.#tasks.get(taskId)?.priority ?? 0,
      abort: (taskId) => {
        this.#tasks.get(taskId)?.controller.abort();
        return true;
      },
    });
  }

  /** @returns true if the store stopped loading tiles because of a permanent error */
  get stopped(): boolean {
    return this.#stopped;
  }

  /**
   * @returns the texture of a tile if loaded
   * @param tile to query
   */
  get(tile: ScanTile): Texture | undefined {
    const key = tileCacheKey(tile);
    const texture = this.#textures.get(key);
    if (texture) {
      this.#textures.delete(key);
      this.#textures.set(key, texture);
    }
    return texture;
  }

  /**
   * @returns true if the tile is loaded or failed permanently, so there is nothing left to wait for
   * @param tile to query
   */
  isSettled(tile: ScanTile): boolean {
    const key = tileCacheKey(tile);
    return this.#textures.has(key) || this.#failedTiles.has(key);
  }

  /**
   * Set the tiles a layer needs, start loading the missing ones and cancel the ones no layer needs anymore
   *
   * @param layer unique identifier of the layer
   * @param tiles needed by the layer, both to load and to keep in cache
   */
  setWanted(layer: symbol, tiles: WantedTile[]): void {
    this.#wanted.set(layer, new Set(tiles.map(tileCacheKey)));
    for (const tile of tiles) {
      const key = tileCacheKey(tile);
      const pending = this.#pending.get(key);
      if (pending) {
        pending.priority = tile.priority;
      } else if (
        !this.#stopped &&
        !this.#textures.has(key) &&
        !this.#failedTiles.has(key)
      ) {
        const taskId = `${this.#nextTaskId++}`;
        const entry = {
          ...tile,
          key,
          taskId,
          controller: new AbortController(),
        };
        this.#pending.set(key, entry);
        this.#tasks.set(taskId, entry);
        this.#manager.run(taskId).then(
          (texture) => this.#onLoaded(entry, texture),
          (error) => this.#onFailed(entry, error),
        );
      }
    }
    this.#cancelUnwanted();
  }

  /**
   * Remove the tiles needed by a layer
   *
   * @param layer to remove
   */
  release(layer: symbol): void {
    this.#wanted.delete(layer);
    this.#cancelUnwanted();
  }

  /** Abort all requests and release all textures */
  dispose(): void {
    this.#stop();
    for (const texture of this.#textures.values()) texture.dispose();
    this.#textures.clear();
  }

  /** Abort all requests and do not accept new ones */
  #stop(): void {
    if (this.#stopped) return;
    this.#stopped = true;
    this.#manager.dispose();
    this.#pending.clear();
    this.#tasks.clear();
  }

  #isWanted(key: string): boolean {
    for (const keys of this.#wanted.values()) {
      if (keys.has(key)) return true;
    }
    return false;
  }

  #cancelUnwanted(): void {
    for (const [key, { taskId }] of this.#pending) {
      if (!this.#isWanted(key)) {
        this.#pending.delete(key);
        this.#manager.cancel(taskId);
      }
    }
  }

  async #load(taskId: string): Promise<Texture> {
    const tile = this.#tasks.get(taskId);
    if (!tile) throw new Error(`Tile task ${taskId} is not pending`);
    const { fetchTile, decode = decodeTileTexture } = this.options;
    const fetchOnce = (): Promise<Blob> =>
      fetchTile(
        {
          projectId: this.options.projectId,
          elementId: this.options.elementId,
          metadataUrl: this.#metadataUrl,
          scans: tile.scans,
          level: tile.level,
          x: tile.x,
          y: tile.y,
        },
        tile.controller.signal,
      );

    try {
      return await decode(await fetchOnce());
    } catch (error) {
      const status = statusOf(error);
      const isServerError = status !== undefined && status >= SERVER_ERROR;
      // A 401 may come from a token expiring during the request, a 5xx from an expired metadata signature
      if (
        status !== UNAUTHORIZED &&
        (!isServerError || this.#metadataRefreshed)
      ) {
        throw error;
      }
      if (isServerError && this.options.refreshMetadataUrl) {
        this.#metadataRefreshed = true;
        this.#metadataUrl = await this.options.refreshMetadataUrl();
      }
      return await decode(await fetchOnce());
    }
  }

  /**
   * @returns true if the request was still the current one for its tile
   * @param entry the finished request
   */
  #settle(entry: PendingTile): boolean {
    this.#tasks.delete(entry.taskId);
    if (this.#pending.get(entry.key) !== entry) return false;
    this.#pending.delete(entry.key);
    return true;
  }

  #onLoaded(entry: PendingTile, texture: Texture): void {
    if (!this.#settle(entry) || this.#stopped) {
      texture.dispose();
      return;
    }
    this.#textures.set(entry.key, texture);
    this.#evict();
  }

  #onFailed(entry: PendingTile, error: unknown): void {
    if (!this.#settle(entry) || this.#stopped || isAbortError(error)) return;

    const status = statusOf(error);
    if (status === UNAUTHORIZED || status === FORBIDDEN) {
      this.#stop();
      console.error("Stopped loading the dynamic overview map", error);
      return;
    }
    this.#failedTiles.add(entry.key);
    console.error(`Failed to load dynamic map tile ${entry.key}`, error);
  }

  #evict(): void {
    const maxTiles = Math.floor(
      this.options.budgetBytes / this.options.tileBytes,
    );
    for (const [key, texture] of this.#textures) {
      if (this.#textures.size <= maxTiles) return;
      if (this.#isWanted(key)) continue;
      texture.dispose();
      this.#textures.delete(key);
    }
  }
}

function statusOf(error: unknown): number | undefined {
  return error instanceof ApiResponseError ? error.status : undefined;
}

function isAbortError(error: unknown): boolean {
  return (
    error instanceof AbortError ||
    (error instanceof Error && error.name === "AbortError")
  );
}

/**
 * @returns a texture from an encoded tile image, with image row 0 (north) at the top of the uv space
 * @param blob the encoded image
 */
export async function decodeTileTexture(blob: Blob): Promise<Texture> {
  const bitmap = await createImageBitmap(blob, {
    imageOrientation: "flipY",
    premultiplyAlpha: "none",
  });
  const texture = new Texture(bitmap);
  texture.flipY = false;
  texture.generateMipmaps = false;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  texture.addEventListener("dispose", () => bitmap.close());
  return texture;
}
